"use client";

import { useEffect, useState, useRef } from "react";
import { usePathname } from "next/navigation";
import { RefreshCw, X } from "lucide-react";

export function ServiceWorkerRegister() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const pathname = usePathname() || "/";
  const isEn = pathname === "/en" || pathname.startsWith("/en/");

  useEffect(() => {
    if (
      process.env.NODE_ENV !== "production" ||
      typeof window === "undefined" ||
      !("serviceWorker" in navigator)
    ) {
      return;
    }

    let refreshing = false;
    let hadController = Boolean(navigator.serviceWorker.controller);

    // Reload when the new service worker takes control (after skipWaiting)
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadController) {
        hadController = true;
        return; // Initial service worker installation; no reload needed
      }
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    navigator.serviceWorker
      .register("/sw.js", {
        scope: "/",
        updateViaCache: "none",
      })
      .then((reg) => {
        registrationRef.current = reg;

        // Check if an updated worker is already waiting in the background
        if (reg.waiting && hadController) {
          setHasUpdate(true);
        }

        // Listen for newly discovered and installed workers
        reg.addEventListener("updatefound", () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              setHasUpdate(true);
            }
          });
        });

        // Periodic check for updates every 30 minutes
        const interval = setInterval(() => {
          reg.update().catch(() => {});
        }, 30 * 60 * 1000);

        // Check whenever the user refocuses the app or returns to the browser tab
        const handleVisibilityChange = () => {
          if (document.visibilityState === "visible") {
            reg.update().catch(() => {});
          }
        };

        const handleFocus = () => {
          reg.update().catch(() => {});
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleFocus);

        return () => {
          clearInterval(interval);
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange,
          );
          window.removeEventListener("focus", handleFocus);
        };
      })
      .catch(() => {
        // SW registration is progressive and non-blocking
      });
  }, []);

  const handleUpdate = () => {
    const reg = registrationRef.current;
    if (reg?.waiting) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
      // Fallback reload if controllerchange doesn't fire within 800ms
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } else {
      window.location.reload();
    }
  };

  if (!hasUpdate || dismissed) {
    return null;
  }

  return (
    <aside
      aria-label={isEn ? "Application Update" : "App-Aktualisierung"}
      className="fixed bottom-5 left-5 z-40 flex items-center gap-3 rounded-full border border-neutral-300 bg-white/95 px-4 py-2 text-xs font-medium text-neutral-900 shadow-xl backdrop-blur-md transition-all sm:bottom-6 sm:left-6 dark:border-neutral-800 dark:bg-neutral-900/95 dark:text-neutral-100"
    >
      <span className="flex items-center gap-1.5">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
        </span>
        <span>
          {isEn ? "Update available" : "Update verfügbar"}
        </span>
      </span>

      <button
        type="button"
        onClick={handleUpdate}
        className="flex items-center gap-1 rounded-full bg-neutral-900 px-3 py-1 font-semibold text-white transition-colors hover:bg-neutral-800 active:scale-95 dark:bg-white dark:text-neutral-900 dark:hover:bg-neutral-200"
      >
        <RefreshCw className="h-3 w-3" />
        <span>{isEn ? "Reload" : "Aktualisieren"}</span>
      </button>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={isEn ? "Dismiss" : "Schließen"}
        className="rounded-full p-1 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-500 dark:hover:bg-neutral-800 dark:hover:text-neutral-300"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </aside>
  );
}
